import useStorage from '@hooks/useStorage';
import enUS from '@arco-design/web-react/es/locale/en-US';
import zhHK from '@arco-design/web-react/es/locale/zh-HK';
import zhCN from '@arco-design/web-react/es/locale/zh-CN';
import { ConfigProvider, Message } from '@arco-design/web-react';
import enUSMobile from '@arco-design/mobile-utils/esm/locale/en-US';
import zhCNMobile from '@arco-design/mobile-utils/esm/locale/zh-CN';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { SERVER_APP_ID } from '@config/constant';
import { useModel } from '@modern-js/runtime/model';
import GlobalStore from '@store/globalStore';
import TrackerSDK, { SDKOptions, TrackEvent, User } from '@hkgai/tracker-sdk';
import { ContextProvider } from '@arco-design/mobile-react';
import { GlobalContext } from './context';
import zhHKMobile from '@/locale/zh-HK/zh-HK';
import { OtherOperateConfig } from '@/config/new.track.config';
import { trackEvent, setGlobalCollectEvent } from '@/utils/pageTracker';

const LAST_VISIT_DATE_KEY = 'last_visit_date';
const TABLE_PAGINATION = {
  showTotal: true,
  sizeCanChange: true,
  hideOnSinglePage: true,
  pageSizeChangeResetCurrent: true,
};
const COMPONENT_CONFIG = {
  Pagination: { showTotal: true, sizeCanChange: true, hideOnSinglePage: false, pageSizeChangeResetCurrent: true, showJumper: false },
  Table: { border: false, stripe: true },
  Drawer: { width: 560, unmountOnExit: true, autoFocus: false, mountOnEnter: false },
  Modal: { autoFocus: false, unmountOnExit: true, focusLock: false },
};

type MeetingTracker = InstanceType<typeof TrackerSDK.HkGaiTrackerSDK>;

export const GlobalProvider = ({ children }: { children: React.ReactNode }) => {
  const [{ userInfo = {} }] = useModel(GlobalStore);
  const [lang, setLang] = useStorage('arco-lang', 'en-US');
  const trackerSDKRef = useRef<MeetingTracker | null>(null);

  const ensureTracker = useCallback(() => {
    if (!trackerSDKRef.current) {
      const sdkOptions: SDKOptions = {
        appId: `${SERVER_APP_ID}`,
        log: false,
        autoTrack: true,
        appName: 'meeting_web',
        channelDomain: '/meeting-admin/api/v1/report',
      };
      trackerSDKRef.current = new TrackerSDK.HkGaiTrackerSDK(sdkOptions);
    }
    return trackerSDKRef.current;
  }, []);

  const getArcoLocale = () => {
    switch (lang) {
      case 'zh-CN':
        return zhCN;
      case 'zh-HK':
        return zhHK;
      default: // 'en-US' and other languages default to English
        return {
          ...enUS,
          Pagination: {
            ...enUS.Pagination,
            goto: 'To',
          },
        };
    }
  };

  const getMobileLocale = () => {
    switch (lang) {
      case 'zh-CN':
        return zhCNMobile;
      case 'en-US':
        return enUSMobile;
      case 'zh-HK':
        return zhHKMobile;
      default:
        return enUSMobile;
    }
  };

  const collectEvent = useCallback((events: TrackEvent[]) => ensureTracker().collectEvent(events), [ensureTracker]);
  const setUserInfo = useCallback(({ meetingOnlyUser: _meetingOnlyUser, ...user }: User & { meetingOnlyUser?: number }) => ensureTracker().setUser(user), [ensureTracker]);
  const contextValue = useMemo(() => ({ lang, setLang, userInfo, collectEvent, setUserInfo }), [collectEvent, lang, setLang, setUserInfo, userInfo]);

  useEffect(() => {
    Message.config({
      duration: 2000,
    });

    setGlobalCollectEvent(collectEvent);
    const today = new Date().toDateString();
    if (localStorage.getItem(LAST_VISIT_DATE_KEY) !== today) {
      localStorage.setItem(LAST_VISIT_DATE_KEY, today);

      trackEvent(OtherOperateConfig.USER_ACTIVE);
    }
    return () => setGlobalCollectEvent(null);
  }, [collectEvent]);

  return (
    <ContextProvider locale={getMobileLocale()}>
      <ConfigProvider locale={getArcoLocale()} tablePagination={TABLE_PAGINATION} componentConfig={COMPONENT_CONFIG}>
        <GlobalContext.Provider value={contextValue}>{children}</GlobalContext.Provider>
      </ConfigProvider>
    </ContextProvider>
  );
};
